import { Box, Button, ButtonProps, Paper, Typography } from "@mui/material";
import React from "react";

type PublicFormCardProps = {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  maxWidth?: number;
  // "success" turns the icon badge green, for confirmation screens.
  tone?: "primary" | "success";
  children: React.ReactNode;
};

const BADGE_TONES = {
  primary: { background: "linear-gradient(135deg, #1976d2, #115293)", shadow: "0 8px 24px rgba(25, 118, 210, 0.3)" },
  success: { background: "linear-gradient(135deg, #2e7d32, #1b5e20)", shadow: "0 8px 24px rgba(46, 125, 50, 0.3)" },
};

// Shared shell for the public-facing forms (login, enquiry, admission verify) so they
// share one look: centred card, icon badge, title and an optional supporting line.
export const PublicFormCard: React.FC<PublicFormCardProps> = ({ icon, title, subtitle, maxWidth = 440, tone = "primary", children }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
      px: 2,
      py: { xs: 4, md: 6 },
      boxSizing: "border-box",
    }}
  >
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth,
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 12px 40px rgba(16, 24, 40, 0.08)",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: "auto",
          mb: 2,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          background: BADGE_TONES[tone].background,
          boxShadow: BADGE_TONES[tone].shadow,
          "& svg": { fontSize: 28 },
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" component="h1" fontWeight={700} align="center">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          {subtitle}
        </Typography>
      )}
      <Box sx={{ mt: 3 }}>{children}</Box>
    </Paper>
  </Box>
);

// The one primary action of a public form card; kept here so every card's button matches.
export const PublicFormButton: React.FC<ButtonProps> = ({ sx, ...props }) => (
  <Button
    fullWidth
    variant="contained"
    {...props}
    sx={[{ py: 1, fontWeight: 600, borderRadius: 2, textTransform: "none" }, ...(Array.isArray(sx) ? sx : [sx])]}
  />
);
